<script lang="ts">
  import { useQueryClient } from '@tanstack/svelte-query';
  import { untrack } from 'svelte';
  import { fa } from '../shared/i18n/fa';
  import { pageLoading } from './progress.svelte';

  /**
   * The one thin bar at the top of the app (ADR-060): a page on its way, a first load of data, or a
   * save in flight. It only reads the query caches and sends nothing. A refresh of data already on
   * screen (live updates, a refetch after a save) does not show it. A wait of a moment shows nothing;
   * progress is never invented, so the bar runs without a percentage and finishes when the work does.
   * A write that never answers cannot pin it: past MAX_MS the bar stands down until the work ends.
   */
  const SHOW_AFTER_MS = 150;
  const MAX_MS = 20_000;
  const FINISH_MS = 340;

  const client = useQueryClient();
  let working = $state(false);
  let phase = $state<'idle' | 'running' | 'finishing'>('idle');

  const check = () => {
    const firstLoad = client.isFetching({ predicate: (q) => q.state.data === undefined }) > 0;
    const saving = client.isMutating({ predicate: (m) => !m.state.isPaused }) > 0;
    working = firstLoad || saving;
  };

  $effect(() => {
    // The caches notify many times per request; one look per tick is enough.
    let queued = false;
    const notify = () => {
      if (queued) return;
      queued = true;
      queueMicrotask(() => {
        queued = false;
        check();
      });
    };
    const offQueries = client.getQueryCache().subscribe(notify);
    const offMutations = client.getMutationCache().subscribe(notify);
    return () => {
      offQueries();
      offMutations();
    };
  });

  const busy = $derived(working || pageLoading.count > 0);
  $effect(() => {
    if (busy) {
      const show = setTimeout(() => (phase = 'running'), SHOW_AFTER_MS);
      const giveUp = setTimeout(() => (phase = 'idle'), MAX_MS);
      return () => {
        clearTimeout(show);
        clearTimeout(giveUp);
      };
    }
    // Read without subscribing: the phase set below must not undo itself.
    if (untrack(() => phase) !== 'running') {
      phase = 'idle';
      return;
    }
    phase = 'finishing';
    const end = setTimeout(() => (phase = 'idle'), FINISH_MS);
    return () => clearTimeout(end);
  });
</script>

{#if phase !== 'idle'}
  <div class="global-progress" data-state={phase} role={phase === 'running' ? 'progressbar' : undefined} aria-label={phase === 'running' ? fa.app.loading : undefined}>
    <div class="global-progress__bar"></div>
  </div>
{/if}
